import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';
import { RecentlyViewedProvider } from './contexts/RecentlyViewedContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import FloatingShopButton from './components/common/FloatingShopButton';
import RecentlyViewedSidebar from './components/common/RecentlyViewedSidebar';
import WhatsAppButton from './components/common/WhatsAppButton';
import Landing from './pages/Landing';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import About from './pages/About';
import Contact from './pages/Contact';
import { Login, Register } from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import AdminPanel from './pages/AdminPanel';
import TechnologyPage from './pages/TechnologyPage';
import UseCaseDetail from './pages/UseCaseDetail';
import './App.css';

// External store URL for e-commerce redirection
const EXTERNAL_STORE_URL = 'https://www.taktikstore.com';

// Redirect to External Store Component
const RedirectToStore = () => {
  React.useEffect(() => {
    window.location.href = EXTERNAL_STORE_URL;
  }, []);
  
  return (
    <div className="min-h-screen bg-vav-bg flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-vav-gold border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-vav-fg-muted mt-4">Mağazaya yönlendiriliyorsunuz...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-vav-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-vav-accent-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`/login?redirect=${location.pathname}`} replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-vav-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-vav-accent-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`/login?redirect=${location.pathname}`} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router with Auth Check
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Navbar />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* Public Routes - Brand Pages */}
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/technology/:slug" element={<TechnologyPage />} />
          <Route path="/use-cases/:slug" element={<UseCaseDetail />} />
          
          {/* Showcase Routes - Redirect to external store */}
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          
          {/* Disabled E-commerce Routes - Redirect to external store */}
          <Route path="/cart" element={
            <RedirectToStore />
          } />
          <Route path="/checkout" element={
            <RedirectToStore />
          } />
          
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Admin Routes - Keep for management */}
          <Route path="/admin/*" element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          } />

          {/* Static Pages */}
          <Route path="/privacy" element={
            <div className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center">
              <p className="text-vav-fg">Gizlilik Politikası - Yakında</p>
            </div>
          } />
          <Route path="/terms" element={
            <div className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center">
              <p className="text-vav-fg">Kullanım Koşulları - Yakında</p>
            </div>
          } />
          <Route path="/kvkk" element={
            <div className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center">
              <p className="text-vav-fg">KVKK - Yakında</p>
            </div>
          } />

          {/* 404 */}
          <Route path="*" element={
            <div className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center">
              <div className="text-center">
                <h1 className="font-heading text-6xl text-vav-gold mb-4">404</h1>
                <p className="text-vav-fg-muted mb-6">Sayfa bulunamadı</p>
                <a href="/" className="tactical-btn">Ana Sayfaya Dön</a>
              </div>
            </div>
          } />
        </Routes>
      </AnimatePresence>
      <FloatingShopButton />
      <WhatsAppButton />
      <Footer />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <SiteSettingsProvider>
            <LanguageProvider>
              <RecentlyViewedProvider>
                <div className="App bg-vav-bg min-h-screen">
                  <AppRouter />
                  <Toaster 
                    position="top-right"
                    toastOptions={{
                      style: {
                        background: '#111111',
                        color: '#ededed',
                        border: '1px solid #27272a',
                      },
                    }}
                  />
                </div>
              </RecentlyViewedProvider>
            </LanguageProvider>
          </SiteSettingsProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
